import { useState, useEffect } from 'react'
import { useAppStore, type Theme, type AIModel } from '../../store/appStore'
import { useAuthStore } from '../../auth/useAuthStore'
import { apiUrl, authFetch } from '../../lib/api'

const MODELS: { id: AIModel; label: string }[] = [
  { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
  { id: 'gpt-4o', label: 'GPT-4o' },
  { id: 'gpt-4o-mini', label: 'GPT-4o Mini' },
  { id: 'claude-4-sonnet', label: 'Claude 4 Sonnet' },
  { id: 'claude-4-opus', label: 'Claude 4 Opus' },
  { id: 'deepseek-v3', label: 'DeepSeek V3' },
  { id: 'llama-4', label: 'Llama 4' },
  { id: 'grok-3', label: 'Grok 3' },
]

export function SettingsPanel({ onClose }: { onClose: () => void }) {
  const { theme, setTheme, aiModel, setAIModel, autoSave, toggleAutoSave } = useAppStore()
  const user = useAuthStore((s) => s.user)
  const [profile, setProfile] = useState<any>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    authFetch(apiUrl('/api/profile')).then((r) => r.json()).then(setProfile).catch(() => {})
  }, [])

  const saveProfile = async (updates: Record<string, any>) => {
    setSaving(true)
    await authFetch(apiUrl('/api/profile'), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    }).catch(() => {})
    setProfile((p: any) => ({ ...p, ...updates }))
    setSaving(false)
  }

  const handleThemeChange = (t: string) => {
    setTheme(t as Theme)
    saveProfile({ theme: t })
  }

  const handleAutoSaveToggle = () => {
    toggleAutoSave()
    saveProfile({ auto_save: !autoSave })
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <span>Settings</span>
          <button onClick={onClose} style={closeBtnStyle}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Section title="Account">
            <Row label="Name" value={user?.name ?? '--'} />
            <Row label="Email" value={user?.email ?? '--'} />
            <Row label="Plan" value={(user?.plan ?? 'free').toUpperCase()} />
            <RowUpgrade currentPlan={user?.plan ?? 'free'} />
            {profile && (
              <>
                <RowInput label="Display Name" value={profile.display_name ?? ''} onSave={(v) => saveProfile({ display_name: v })} />
                <RowInput label="Bio" value={profile.bio ?? ''} onSave={(v) => saveProfile({ bio: v })} />
                <RowInput label="Website" value={profile.website ?? ''} onSave={(v) => saveProfile({ website: v })} />
              </>
            )}
          </Section>

          <Section title="Appearance">
            <RowSelect label="Theme" value={theme} options={[
              { value: 'dark', label: 'Dark' },
              { value: 'light', label: 'Light' },
            ]} onChange={handleThemeChange} />
          </Section>

          <Section title="Editor">
            <RowToggle label="Auto-save" value={autoSave} onChange={handleAutoSaveToggle} />
            {profile && (
              <RowSelect label="Font Size" value={String(profile.font_size ?? 13)} options={[
                { value: '11', label: '11px' }, { value: '12', label: '12px' },
                { value: '13', label: '13px' }, { value: '14', label: '14px' },
                { value: '15', label: '15px' }, { value: '16', label: '16px' },
              ]} onChange={(v) => saveProfile({ font_size: parseInt(v) })} />
            )}
          </Section>

          <Section title="AI">
            <RowSelect label="Default Model" value={aiModel} options={MODELS.map((m) => ({ value: m.id, label: m.label }))} onChange={(v) => setAIModel(v as AIModel)} />
          </Section>

          <Section title="Keyboard Shortcuts">
            <Shortcut keys="Ctrl+S" desc="Save file" />
            <Shortcut keys="Ctrl+K" desc="Command palette" />
            <Shortcut keys="Ctrl+P" desc="File search" />
            <Shortcut keys="Ctrl+Shift+F" desc="Search in files" />
            <Shortcut keys="Ctrl+B" desc="Toggle sidebar" />
            <Shortcut keys="Ctrl+J" desc="Toggle AI panel" />
          </Section>
        </div>

        {saving && <div style={{ padding: '4px 20px', fontSize: 9, color: 'var(--accent-2)', fontFamily: 'var(--font-mono)', borderTop: '1px solid var(--border)' }}>Saving...</div>}
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--text-2)', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 10 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={rowStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={{ fontSize: 12, color: 'var(--text-0)', fontFamily: 'var(--font-mono)' }}>{value}</span>
    </div>
  )
}

function RowInput({ label, value, onSave }: { label: string; value: string; onSave: (v: string) => void }) {
  const [val, setVal] = useState(value)
  return (
    <div style={rowStyle}>
      <span style={labelStyle}>{label}</span>
      <input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => { if (val !== value) onSave(val) }}
        onKeyDown={(e) => { if (e.key === 'Enter') { (e.target as HTMLInputElement).blur() } }}
        style={{
          padding: '3px 8px', borderRadius: 4, border: '1px solid var(--border-2)',
          background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)',
          fontSize: 11, outline: 'none', width: 160,
        }}
      />
    </div>
  )
}

function RowSelect({ label, value, options, onChange }: { label: string; value: string; options: { value: string; label: string }[]; onChange: (v: string) => void }) {
  return (
    <div style={rowStyle}>
      <span style={labelStyle}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{
        padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border-2)',
        background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)',
        fontSize: 11, outline: 'none', cursor: 'pointer',
      }}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )
}

function RowToggle({ label, value, onChange }: { label: string; value: boolean; onChange: () => void }) {
  return (
    <div style={rowStyle}>
      <span style={labelStyle}>{label}</span>
      <button onClick={onChange} style={{
        width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
        background: value ? 'var(--accent-2)' : 'var(--bg-4)', position: 'relative', transition: 'background 0.15s',
      }}>
        <span style={{
          position: 'absolute', top: 2, left: value ? 18 : 2,
          width: 16, height: 16, borderRadius: '50%', background: '#fff',
          transition: 'left 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        }} />
      </button>
    </div>
  )
}

function Shortcut({ keys, desc }: { keys: string; desc: string }) {
  return (
    <div style={{ ...rowStyle, padding: '4px 0' }}>
      <span style={labelStyle}>{desc}</span>
      <kbd style={{
        padding: '2px 8px', borderRadius: 4, background: 'var(--bg-3)',
        border: '1px solid var(--border-2)', fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-0)',
      }}>{keys}</kbd>
    </div>
  )
}

function RowUpgrade({ currentPlan }: { currentPlan: string }) {
  const upgradePlan = useAuthStore(s => s.upgradePlan)
  const [upgrading, setUpgrading] = useState(false)

  if (currentPlan === 'enterprise') return null

  const nextPlan = currentPlan === 'free' ? 'pro' : currentPlan === 'pro' ? 'unlimited' : 'enterprise'
  const prices: Record<string, string> = { pro: '$20/mo', unlimited: '$49/mo', enterprise: '$99/mo' }

  const handleUpgrade = async () => {
    setUpgrading(true)
    await upgradePlan(nextPlan as any)
    setUpgrading(false)
    window.location.reload()
  }

  return (
    <div style={rowStyle}>
      <span style={labelStyle}>Upgrade</span>
      <button onClick={handleUpgrade} disabled={upgrading} style={{
        padding: '4px 12px', borderRadius: 6, border: '1px solid var(--accent)',
        background: 'rgba(124,110,245,0.1)', color: 'var(--accent)', cursor: 'pointer',
        fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
      }}>
        {upgrading ? 'Upgrading...' : `${nextPlan.charAt(0).toUpperCase() + nextPlan.slice(1)} — ${prices[nextPlan]}`}
      </button>
    </div>
  )
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 998, background: 'rgba(0,0,0,0.5)',
  display: 'flex', justifyContent: 'center', alignItems: 'center',
}
const panelStyle: React.CSSProperties = {
  width: 460, maxWidth: '90vw', maxHeight: '80vh', background: 'var(--bg-1)',
  border: '1px solid var(--border-2)', borderRadius: 14,
  boxShadow: '0 16px 48px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
}
const headerStyle: React.CSSProperties = {
  padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex',
  alignItems: 'center', justifyContent: 'space-between', fontFamily: 'var(--font-display)',
  fontWeight: 700, fontSize: 13, color: 'var(--text-0)', flexShrink: 0,
}
const closeBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', display: 'flex', padding: 4, borderRadius: 4,
}
const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
}
const labelStyle: React.CSSProperties = {
  fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--font-mono)',
}
